import { Id } from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import * as FileSystem from 'expo-file-system/legacy'
import { resolveMime } from 'friendly-mimes'
import { Platform } from 'react-native'
import keys from './shared/keys'

export function useFileContent(
  fileId: Id,
  versionNumber?: number,
  mimeType?: string,
  fileUrl?: string,
  enabled?: boolean
) {
  const { server } = useServer()
  return useQuery({
    queryKey: keys.content(fileId, versionNumber),
    queryFn: fetchFileContent,
    enabled: enabled && !!fileId && !!fileUrl && !!versionNumber,
  })

  async function fetchFileContent() {
    if (!fileUrl) {
      throw new Error('File URL missing')
    }
    if (!mimeType) {
      throw new Error('MIME type missing')
    }
    const fileName = `${fileId}_${versionNumber}${
      resolveMime(mimeType).fileType
    }`
    const existingFile = await checkExistingFile(fileName)
    const hasAccess = await verifyFileAccess()
    if (!hasAccess) {
      // If access is denied, delete the cached file
      await deleteCachedFile(fileName)
      throw new Error('Access denied')
    }

    if (existingFile) {
      return existingFile
    }

    // If file doesn't exist locally, download it and save it to disk
    const localFile = saveFileToDisk(fileUrl, mimeType, fileName)
    return localFile
  }

  async function verifyFileAccess(): Promise<boolean> {
    try {
      const response = await server.get<boolean>(
        `/v2/chat/repository/files/${fileId}/verify_access`
      )
      return response
    } catch (error) {
      console.error('Error verifying file access:', error)
      return false
    }
  }
}

async function checkExistingFile(
  fileName: string
): Promise<{ url: string; contentType: string } | undefined> {
  if (Platform.OS === 'web') {
    return
  }

  const fileUri = `${FileSystem.documentDirectory}${fileName}`
  const fileInfo = await FileSystem.getInfoAsync(fileUri)

  if (fileInfo.exists) {
    try {
      const metadata = await FileSystem.readAsStringAsync(`${fileUri}.meta`)
      const { contentType } = JSON.parse(metadata)
      return { url: fileUri, contentType }
    } catch (error) {
      console.error('Error reading file metadata:', error)
      return
    }
  }
  return
}

async function deleteCachedFile(fileName: string) {
  if (Platform.OS === 'web') return

  const fileUri = `${FileSystem.documentDirectory}${fileName}`
  try {
    await FileSystem.deleteAsync(fileUri)
    await FileSystem.deleteAsync(`${fileUri}.meta`)
  } catch (error) {
    console.error('Error deleting cached file:', error)
  }
}

async function saveFileToDisk(
  fileUrl: string,
  contentType: string,
  fileName: string
): Promise<{
  url: string
  contentType: string
}> {
  let url: string

  if (Platform.OS === 'web') {
    try {
      const response = await fetch(fileUrl)
      const byteArray = new Uint8Array(await response.arrayBuffer())
      const blob = new Blob([byteArray], { type: contentType })
      url = URL.createObjectURL(blob)
    } catch (error) {
      console.error('Error downloading file:', error)
      throw new Error('Error downloading file')
    }
  } else {
    const localUri = `${FileSystem.documentDirectory}${fileName}`
    const result = await FileSystem.downloadAsync(fileUrl, localUri)
    if (result.status !== 200) {
      throw new Error('Error downloading file')
    }
    await FileSystem.writeAsStringAsync(
      `${localUri}.meta`,
      JSON.stringify({ contentType }),
      { encoding: FileSystem.EncodingType.UTF8 }
    )
    url = localUri
  }
  return { url, contentType }
}
